/**
 * useContextMenuActions - Handles context menu actions for the automation flow canvas.
 *
 * Extracts the delete and duplicate handlers from the main canvas component
 * to reduce complexity and improve testability.
 */

import type * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import type { BaseNodeData } from '../types';
import type { HistoryActionType } from './useUndoRedo';
import { moveNodeAfter } from '../utils/nodeReordering';
import { isAutomationStepNodeData } from '../types/typeGuards';
import { isTriggerAutomationStepNode, TRIGGER_AUTOMATION_LAST_STEP_TOAST } from '../utils/triggerAutomationStepRules';
import { BEHAVIOR_SWITCH_LAST_STEP_MESSAGE, templateHasBehaviorSwitch } from '../utils/behaviorSwitchRules';

interface UseContextMenuActionsParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	copySelection: (nodeIds: string[]) => void;
	pasteAtPosition: (position: { x: number; y: number }) => { nodes: Node<BaseNodeData>[]; edges: Edge[] } | null;
	/** Move a step up by swapping with its predecessor */
	moveUp?: (nodeId: string) => boolean;
	/** Move a step down by swapping with its successor */
	moveDown?: (nodeId: string) => boolean;
	/**
	 * Callback to request deletion of the given nodes. Opens a confirmation dialog
	 * instead of deleting immediately.
	 */
	onRequestDeleteNodes?: (nodeIds: string[]) => void;
	/** Callback to persist a duplication to the API */
	onDuplicateStep?: (params: {
		templateId: string;
		stepType: Api.AutomationStepType;
		step: Api.IAutomationStep;
		beforeId?: string;
	}) => Promise<void>;
}

function findSuccessorStepId(nodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]): string | undefined {
	const successorNodeId = edges.find(edge => edge.source === nodeId)?.target;
	const successor = successorNodeId ? nodes.find(node => node.id === successorNodeId) : undefined;
	return successor && isAutomationStepNodeData(successor.data) ? successor.data.stepId : undefined;
}

interface ContextMenuActions {
	handleDelete: (nodeId: string) => void;
	handleDuplicate: (nodeId: string) => void;
	handleMoveUp: (nodeId: string) => void;
	handleMoveDown: (nodeId: string) => void;
}

export function useContextMenuActions({
	nodes,
	edges,
	setNodes,
	setEdges,
	takeSnapshot,
	copySelection,
	pasteAtPosition,
	moveUp,
	moveDown,
	onRequestDeleteNodes,
	onDuplicateStep,
}: UseContextMenuActionsParams): ContextMenuActions {
	const handleDelete = useCallback(
		(nodeId: string) => {
			// Defer to the shared confirmation flow; the actual removal + persistence runs
			// only after the user confirms (see useDeleteConfirmation).
			onRequestDeleteNodes?.([nodeId]);
		},
		[onRequestDeleteNodes]
	);

	const handleDuplicate = useCallback(
		(nodeId: string) => {
			const nodeToDuplicate = nodes.find((n: Node<BaseNodeData>) => n.id === nodeId);
			if (!nodeToDuplicate) {
				return;
			}

			if (isTriggerAutomationStepNode(nodeToDuplicate)) {
				toast.error(TRIGGER_AUTOMATION_LAST_STEP_TOAST);
				return;
			}

			// A copy with no step after it goes to the end of its template, past any Conditional Behavior.
			const successorStepId = findSuccessorStepId(nodeId, nodes, edges);
			const ownerTemplate = (nodeToDuplicate.data as { automationTemplate?: { id?: string } }).automationTemplate;
			if (!successorStepId && ownerTemplate?.id && templateHasBehaviorSwitch(nodes, ownerTemplate.id)) {
				toast.error(BEHAVIOR_SWITCH_LAST_STEP_MESSAGE);
				return;
			}

			// Optimistically duplicate locally and insert directly after the node.
			copySelection([nodeId]);

			const nodeHeight = nodeToDuplicate.measured?.height || 100;
			const pastePosition = {
				x: nodeToDuplicate.position.x,
				y: nodeToDuplicate.position.y + nodeHeight + 100,
			};

			const pasteResult = pasteAtPosition(pastePosition);
			if (!pasteResult || pasteResult.nodes.length !== 1) {
				return;
			}

			takeSnapshot('paste', `Duplicate ${nodeToDuplicate.data.label || 'node'}`);

			const newNode = pasteResult.nodes[0];
			const nodesWithNew = [...nodes, newNode];
			const edgesWithNew = [...edges, ...pasteResult.edges];

			const moved = moveNodeAfter(nodesWithNew, edgesWithNew, newNode.id, nodeId);
			if (moved.success) {
				setNodes(moved.nodes.map(n => ({ ...n, selected: n.id === newNode.id })));
				setEdges(moved.edges);
				toast.success('Node duplicated');
			}

			// Persist duplication to the API (only supported for saved step nodes).
			if (onDuplicateStep && isAutomationStepNodeData(nodeToDuplicate.data)) {
				const stepId = nodeToDuplicate.data.stepId;
				const templateId = nodeToDuplicate.data.automationTemplate?.id;
				const stepType = nodeToDuplicate.data.stepType;
				const step = nodeToDuplicate.data.automationStep;
				if (stepId && templateId && step) {
					onDuplicateStep({ templateId, stepType, step, beforeId: successorStepId }).catch(() => {
						toast.error('Failed to duplicate step on server');
					});
				}
			}
		},
		[nodes, edges, copySelection, pasteAtPosition, takeSnapshot, setNodes, setEdges, onDuplicateStep]
	);

	const handleMoveUp = useCallback(
		(nodeId: string) => {
			if (moveUp) {
				moveUp(nodeId);
			}
		},
		[moveUp]
	);

	const handleMoveDown = useCallback(
		(nodeId: string) => {
			if (moveDown) {
				moveDown(nodeId);
			}
		},
		[moveDown]
	);

	return {
		handleDelete,
		handleDuplicate,
		handleMoveUp,
		handleMoveDown,
	};
}
